import { useEffect, useState } from 'react';
import axios from 'axios';
import { Flame } from 'lucide-react';
import { roundLabel } from '../utils/wcLabels';
import useCompetitionStatus from '../hooks/useCompetitionStatus';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SPORT_LABELS = { AFL: 'AFL', NRL: 'NRL', EPL: 'EPL', AL: 'A-League', WC: 'World Cup' };
const BADGE = { AFL: 'tipster-badge-afl', NRL: 'tipster-badge-nrl', EPL: 'tipster-badge-epl', AL: 'tipster-badge-al', WC: 'tipster-badge-wc' };
const PLACE = {
  1: { bg: 'linear-gradient(135deg,#FFE27A,#E0A800)', fg: '#3D2A00', name: 'Gold' },
  2: { bg: 'linear-gradient(135deg,#F1F3F5,#A7ADB4)', fg: '#1F2328', name: 'Silver' },
  3: { bg: 'linear-gradient(135deg,#E9A46A,#A0582A)', fg: '#2B1405', name: 'Bronze' },
};
const OTHER = { bg: '#111111', fg: '#FFFFFF', name: 'Outside top 3' };

function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function FormBlock({ sport, block, index }) {
  const style = (block.tipped && PLACE[block.rank]) || OTHER;
  const pts = (Math.round(block.points * 100) / 100).toFixed(2).replace(/\.00$/, '');
  return (
    <div
      className="form-block flex-1 min-w-[4.5rem] rounded-sm px-2 py-2 text-center"
      style={{ background: style.bg, color: style.fg, animationDelay: `${index * 70}ms` }}
      title={`${roundLabel(sport, block.round_number)} · ${block.tipped ? `${ordinal(block.rank)} of ${block.tipsters}` : 'No tips'} · ${style.name}`}
      data-testid={`form-block-${sport}-${block.round_number}`}
    >
      <div className="text-[9px] font-bold uppercase tracking-widest opacity-70 truncate">{roundLabel(sport, block.round_number)}</div>
      <div className="text-lg font-black tabular-nums leading-tight" data-testid={`form-block-points-${sport}-${block.round_number}`}>{pts}</div>
      <div className="text-[10px] font-semibold opacity-80">{block.tipped ? ordinal(block.rank) : '—'}</div>
    </div>
  );
}

export default function FormGuideStrip({ currentUserId }) {
  const [data, setData] = useState(null);
  const compStatus = useCompetitionStatus();
  const closed = new Set(compStatus?.closed_sports || []);

  useEffect(() => {
    if (!currentUserId) return;
    axios.get(`${API_URL}/api/dashboard/form-guide`, { withCredentials: true })
      .then((res) => setData(res.data))
      .catch(() => setData(null));
  }, [currentUserId]);

  const sports = Object.keys(SPORT_LABELS).filter((s) => !closed.has(s) && data?.[s]?.length);
  if (!currentUserId || sports.length === 0) return null;

  return (
    <section className="mb-8" data-testid="form-guide-strip">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gray-500">
          <Flame className="w-3.5 h-3.5 text-[#FF6B00]" /> Your form · last 5 rounds
        </div>
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-wider text-gray-500">
          {[PLACE[1], PLACE[2], PLACE[3], OTHER].map((p) => (
            <span key={p.name} className="inline-flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: p.bg }} />{p.name}
            </span>
          ))}
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-x-8 gap-y-3">
        {sports.map((s) => (
          <div key={s} className="flex items-center gap-3" data-testid={`form-guide-${s}`}>
            <span className={`${BADGE[s]} w-20 text-center flex-shrink-0`}>{SPORT_LABELS[s]}</span>
            <div className="flex gap-1.5 flex-1">
              {data[s].map((b, i) => <FormBlock key={b.round_number} sport={s} block={b} index={i} />)}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
